import React, { useEffect, useState, useRef, useMemo } from "react";
import { PlusCircle, Trash2, ImageIcon, Edit2, X, Search, ChevronLeft, ChevronRight, Eye, Newspaper } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

export default function AdminNews() {
  const navigate = useNavigate();
  const [newsList, setNewsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    title: "",
    excerpt: "",
    description: "",
    date: "",
    author: "",
    category: "",
    link: "",
  });
  const [file, setFile] = useState(null);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [animateModal, setAnimateModal] = useState(false);
  const [animatePage, setAnimatePage] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  // Detail modal state
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [detailNews, setDetailNews] = useState(null);

  // Request count state
  const [requestCount, setRequestCount] = useState(0);

  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const modalRef = useRef(null);

  // Filter and paginate news
  const filteredNews = useMemo(() => 
    newsList.filter(news => 
      Object.values(news).some(
        value => 
          value && 
          value.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    ),
    [newsList, searchTerm]
  );

  const totalPages = Math.ceil(filteredNews.length / itemsPerPage);
  const paginatedNews = useMemo(
    () => filteredNews.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    ),
    [filteredNews, currentPage, itemsPerPage]
  );

  const fetchNews = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        "https://aaapd.org.et/gallery_backend/news/getNews.php"
      );
      setNewsList(res.data);
    } catch (err) {
      console.error(err);
      showMessage("Failed to fetch news.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setAnimatePage(true);
    fetchNews();
    fetchRequestCount();
    // Poll for request count every 30 seconds
    const interval = setInterval(fetchRequestCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchRequestCount = async () => {
    try {
      const res = await axios.get(
        "https://aaapd.org.et/gallery_backend/news/getAllNewsForAdmin.php"
      );
      if (Array.isArray(res.data)) {
        const pendingCount = res.data.filter(news => news.status === "pending").length;
        setRequestCount(pendingCount);
      }
    } catch (err) {
      console.error("Error fetching request count:", err);
    }
  };

  const showMessage = (msg, type = "success") => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(""), 5000);
  };

  const openModal = (news = null) => {
    if (news) {
      setIsEditMode(true);
      setEditId(news.id);
      setFormData({
        title: news.title,
        excerpt: news.excerpt,
        description: news.description,
        date: news.date,
        author: news.author,
        category: news.category,
        link: news.link,
      });
    } else {
      setIsEditMode(false);
      setFormData({
        title: "",
        excerpt: "",
        description: "",
        date: "",
        author: "",
        category: "",
        link: "",
      });
    }
    setFile(null);
    setModalPosition({ x: 0, y: 0 });
    setIsModalOpen(true);
    setTimeout(() => setAnimateModal(true), 10);
  };

  const closeModal = () => {
    setAnimateModal(false);
    setTimeout(() => setIsModalOpen(false), 300);
  };

  const openDetailModal = (news) => {
    setDetailNews(news);
    setIsDetailModalOpen(true);
  };

  const closeDetailModal = () => {
    setIsDetailModalOpen(false);
    setDetailNews(null);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    dragStart.current = {
      x: e.clientX - modalPosition.x,
      y: e.clientY - modalPosition.y,
    };
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setModalPosition({
      x: e.clientX - dragStart.current.x,
      y: e.clientY - dragStart.current.y,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const url = isEditMode
      ? "https://aaapd.org.et/gallery_backend/news/editNews.php"
      : "https://aaapd.org.et/gallery_backend/news/addNews.php";

    const data = new FormData();
    Object.keys(formData).forEach((key) => {
      data.append(key, formData[key]);
    });
    if (file) data.append("image", file);
    if (isEditMode) data.append("id", editId);

    try {
      const res = await axios.post(url, data);
      showMessage(res.data.message);
      closeModal();
      fetchNews();
    } catch (err) {
      console.error(err);
      showMessage("Failed to save news.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this news?")) return;

    try {
      const res = await axios.post(
        "https://aaapd.org.et/gallery_backend/news/deleteNews.php",
        new URLSearchParams({ id })
      );
      showMessage(res.data.message);
      fetchNews();
    } catch (err) {
      console.error(err);
      showMessage("Failed to delete news.");
    }
  };

  return (
    <div
      className={`bg-gray-50 min-h-screen p-6 transition-opacity duration-1000 ease-in-out ${
        animatePage ? "opacity-100" : "opacity-0"
      }`}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      {/* Header */}
      <div className="max-w-7xl mx-auto">
        <header className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">News Management</h1>
            <p className="text-gray-600 mt-1">Manage and organize your news articles</p>
          </div>
          <div className="flex gap-3">

<button
  onClick={() => navigate("/adminapprovalnews")}
  className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2.5 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 relative"
>
  <span>Requests</span>
  {requestCount > 0 && (
    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center animate-pulse">
      {requestCount}
    </span>
  )}
</button>

            <button
              onClick={() => openModal()}
              className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2.5 rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
            >
              <PlusCircle className="w-5 h-5" /> 
              <span>Add New Article</span>
            </button>
          </div>
        </header>

        {/* Message */}
        {message && (
          <div 
            className={`mb-6 p-4 rounded-lg ${
              messageType === "error" 
                ? "bg-red-100 border-l-4 border-red-500 text-red-700" 
                : "bg-green-100 border-l-4 border-green-500 text-green-700"
            }`}
          >
            {message}
          </div>
        )}

        {/* Search and Filter */}
        <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
          <div className="relative max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search news articles..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
            />
          </div>
        </div>

        {/* Add/Edit Modal */}
        {isModalOpen && (
          <div
            className={`fixed inset-0 flex items-center justify-center z-50 transition-opacity duration-300 ${
              animateModal ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ 
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)'
            }}
          >
            <div
              ref={modalRef}
              onMouseDown={handleMouseDown}
              style={{
                transform: `translate(${modalPosition.x}px, ${modalPosition.y}px)`,
              }}
              className={`bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 transform transition-all duration-300 ease-in-out ${
                animateModal ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
              } ${isDragging ? 'cursor-grabbing' : 'cursor-move'}`}
            >
              <div className="px-5 py-3 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                  {isEditMode ? (
                    <>
                      <Edit2 className="w-4 h-4 text-teal-600" /> Edit Article
                    </>
                  ) : (
                    <>
                      <PlusCircle className="w-4 h-4 text-teal-600" /> Add New Article
                    </>
                  )}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form
                className="p-5 space-y-3 max-h-[70vh] overflow-y-auto"
                onSubmit={handleSubmit}
              >
                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-600">
                    Image File
                  </label>
                  <input
                    type="file"
                    onChange={(e) => setFile(e.target.files[0])}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:font-medium file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-600">
                    Title *
                  </label>
                  <input
                    type="text"
                    placeholder="Enter news title"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 text-sm"
                    required
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-600">
                    Excerpt *
                  </label>
                  <input
                    type="text"
                    placeholder="Short summary"
                    value={formData.excerpt}
                    onChange={(e) =>
                      setFormData({ ...formData, excerpt: e.target.value })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 text-sm"
                    required
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-600">
                    Description
                  </label>
                  <textarea
                    placeholder="Full description"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 text-sm"
                    rows={2}
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-600">
                    Date
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) =>
                      setFormData({ ...formData, date: e.target.value })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 text-sm"
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-600">
                    Author
                  </label>
                  <input
                    type="text"
                    placeholder="Author name"
                    value={formData.author}
                    onChange={(e) =>
                      setFormData({ ...formData, author: e.target.value })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 text-sm"
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-600">
                    Category
                  </label>
                  <input
                    type="text"
                    placeholder="News category"
                    value={formData.category}
                    onChange={(e) =>
                      setFormData({ ...formData, category: e.target.value })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 text-sm"
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-600">
                    External Link
                  </label>
                  <input
                    type="text"
                    placeholder="https://example.com/news"
                    value={formData.link}
                    onChange={(e) =>
                      setFormData({ ...formData, link: e.target.value })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 text-sm"
                  />
                </div>

                <div className="pt-3 flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="inline-flex justify-center py-1.5 px-3 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="inline-flex justify-center py-1.5 px-3 border border-transparent shadow-sm text-xs font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                  >
                    {isEditMode ? 'Save Changes' : 'Add Article'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* News Table */}
        <div className="bg-white shadow overflow-hidden rounded-lg">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Author
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Image
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-teal-500"></div>
                      </div>
                      <p className="mt-2 text-sm text-gray-500">Loading news articles...</p>
                    </td>
                  </tr>
                ) : paginatedNews.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center">
                      <Newspaper className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No articles found</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        {searchTerm 
                          ? "No articles match your search. Try a different term."
                          : "Get started by adding a new article."}
                      </p>
                    </td>
                  </tr>
                ) : (
                  paginatedNews.map((news) => (
                    <tr key={news.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900 max-w-xs truncate">
                          {news.title}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{news.author || <span className="text-gray-400">N/A</span>}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{news.date || <span className="text-gray-400">N/A</span>}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-teal-100 text-teal-800">
                          {news.category || 'General'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex-shrink-0 h-16 w-16">
                          {news.image ? (
                            <img
                              className="h-16 w-16 rounded-md object-cover border border-gray-200"
                              src={
                                news.image.includes('http')
                                  ? news.image
                                  : `https://aaapd.org.et/gallery_backend/news_opd/${news.image}`
                              }
                              alt={news.title || 'News image'}
                            />
                          ) : (
                            <div className="h-16 w-16 rounded-md bg-gray-100 flex items-center justify-center border border-gray-200">
                              <ImageIcon className="h-8 w-8 text-gray-400" />
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => openDetailModal(news)}
                            className="text-teal-600 hover:text-teal-900 p-1.5 rounded-md hover:bg-teal-50 transition-colors"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => openModal(news)}
                            className="text-blue-600 hover:text-blue-900 p-1.5 rounded-md hover:bg-blue-50 transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(news.id)}
                            className="text-red-600 hover:text-red-900 p-1.5 rounded-md hover:bg-red-50 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-6 py-3 flex items-center justify-between border-t border-gray-200">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                    currentPage === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                    currentPage === totalPages
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(currentPage * itemsPerPage, filteredNews.length)}
                    </span>{' '}
                    of <span className="font-medium">{filteredNews.length}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                      disabled={currentPage === 1}
                      className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                        currentPage === 1
                          ? 'text-gray-300 cursor-not-allowed'
                          : 'text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      <span className="sr-only">Previous</span>
                      <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                    </button>
                    
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === pageNum
                              ? 'z-10 bg-teal-50 border-teal-500 text-teal-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                        currentPage === totalPages
                          ? 'text-gray-300 cursor-not-allowed'
                          : 'text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      <span className="sr-only">Next</span>
                      <ChevronRight className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Detail Modal */}
        {isDetailModalOpen && detailNews && (
          <div
            className="fixed inset-0 flex items-center justify-center z-50"
            style={{ 
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)'
            }}
          >
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4">
              <div className="px-5 py-3 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                  <Eye className="w-4 h-4 text-teal-600" />
                  Article Details
                </h3>
                <button
                  onClick={closeDetailModal}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
                {detailNews.image && (
                  <div className="flex justify-center bg-gray-50 rounded-lg p-2">
                    <img
                      src={
                        detailNews.image.includes('http')
                          ? detailNews.image
                          : `https://aaapd.org.et/gallery_backend/news_opd/${detailNews.image}`
                      }
                      alt={detailNews.title || 'News image'}
                      className="max-h-32 max-w-full object-contain rounded border border-gray-200"
                    />
                  </div>
                )}

                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-600">
                    Title
                  </label>
                  <p className="text-sm text-gray-900 bg-gray-50 px-2 py-1.5 rounded border border-gray-200">
                    {detailNews.title}
                  </p>
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-600">
                    Excerpt
                  </label>
                  <p className="text-sm text-gray-900 bg-gray-50 px-2 py-1.5 rounded border border-gray-200">
                    {detailNews.excerpt || <span className="text-gray-400 italic">No excerpt</span>}
                  </p>
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-600">
                    Description
                  </label>
                  <p className="text-sm text-gray-900 bg-gray-50 px-2 py-1.5 rounded border border-gray-200 min-h-[50px]">
                    {detailNews.description || <span className="text-gray-400 italic">No description</span>}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="block text-xs font-medium text-gray-600">
                      Author
                    </label>
                    <p className="text-sm text-gray-900 bg-gray-50 px-2 py-1.5 rounded border border-gray-200">
                      {detailNews.author || <span className="text-gray-400">N/A</span>}
                    </p>
                  </div>

                  <div className="space-y-1">
                    <label className="block text-xs font-medium text-gray-600">
                      Date
                    </label>
                    <p className="text-sm text-gray-900 bg-gray-50 px-2 py-1.5 rounded border border-gray-200">
                      {detailNews.date || <span className="text-gray-400">N/A</span>}
                    </p>
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-600">
                    Category
                  </label>
                  <p className="text-sm text-gray-900 bg-gray-50 px-2 py-1.5 rounded border border-gray-200">
                    {detailNews.category || 'General'}
                  </p>
                </div>

                {detailNews.link && (
                  <div className="space-y-1">
                    <label className="block text-xs font-medium text-gray-600">
                      External Link
                    </label>
                    <a 
                      href={detailNews.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-teal-600 hover:text-teal-800 bg-gray-50 px-2 py-1.5 rounded border border-gray-200 block break-all"
                    >
                      {detailNews.link}
                    </a>
                  </div>
                )}

                <div className="pt-3 flex justify-end space-x-2">
                  <button
                    onClick={closeDetailModal}
                    className="inline-flex justify-center py-1.5 px-3 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      closeDetailModal();
                      openModal(detailNews);
                    }}
                    className="inline-flex items-center gap-1.5 py-1.5 px-3 border border-transparent shadow-sm text-xs font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                  >
                    <Edit2 className="h-3.5 w-3.5" />
                    Edit
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
